import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getElseworldForumsPath } from '../../utils/universePaths';
import styles from './ElseworldCard.module.css';

const ElseworldCard = ({
  elseworld,
  universeSlug,
  hasUnread = false,
  hasParticipatingHighlight = false,
}) => {
  const navigate = useNavigate();
  const lastThread = elseworld.recentThreads?.[0] || (elseworld.lastPost ? {
    threadId: elseworld.lastPost.threadId,
    threadSlug: elseworld.lastPost.threadSlug,
    threadTitle: elseworld.lastPost.threadTitle,
    author: elseworld.lastPost.author,
    character: elseworld.lastPost.character,
    avatar: elseworld.lastPost.avatar,
    date: elseworld.lastPost.date,
  } : null);
  const cardPath = getElseworldForumsPath(universeSlug, elseworld.slug);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const handleThreadClick = (e, thread) => {
    const target = thread.threadSlug || thread.threadId;
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/threads/${target}`);
  };

  return (
    <Link
      to={cardPath}
      className={`${styles.elseworldCard} ${hasUnread ? styles.unread : ''} ${hasParticipatingHighlight ? styles.participatingHighlight : ''}`}
    >
      {elseworld.banner && (
        <div
          className={styles.cardBanner}
          style={{ backgroundImage: `url("${elseworld.banner}")` }}
        />
      )}
      <div className={styles.cardOverlay} />
      <div className={styles.multiversePattern} aria-hidden="true" />

      <div className={styles.cardContent}>
        <div className={styles.cardInfo}>
          <div className={styles.cardHeader}>
            {elseworld.logo && (
              <img
                src={elseworld.logo}
                alt=""
                className={styles.cardLogo}
              />
            )}
            <div className={styles.cardHeading}>
              <span className={styles.elseworldBadge}>Elseworld</span>
              <h3 className={styles.cardTitle}>{elseworld.name}</h3>
              <p className={styles.realityLabel}>Réalité alternative</p>
            </div>
          </div>

          {elseworld.description && (
            <p className={styles.cardDesc}>{elseworld.description}</p>
          )}

          <div className={styles.cardMeta}>
            <span>{elseworld.stats?.forumCount || 0} forum{(elseworld.stats?.forumCount || 0) > 1 ? 's' : ''}</span>
            <span className={styles.metaDot}>•</span>
            <span>{elseworld.stats?.totalThreads || 0} discussions</span>
            <span className={styles.metaDot}>•</span>
            <span>{elseworld.stats?.totalPosts || 0} messages</span>
            {(hasUnread || hasParticipatingHighlight) && (
              <div className={styles.metaBadges}>
                {hasUnread && <span className={styles.unreadPill}>Nouveau</span>}
                {hasParticipatingHighlight && <span className={styles.participatingPill}>Participation</span>}
              </div>
            )}
          </div>
        </div>

        {lastThread && (lastThread.threadSlug || lastThread.threadId) ? (
          <div
            className={styles.cardSidebar}
            onClick={(e) => handleThreadClick(e, lastThread)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              handleThreadClick(e, lastThread);
            }}
          >
            <div className={styles.sidebarLabel}>Activité</div>
            {lastThread.threadTitle && (
              <div className={styles.sidebarThreadTitle}>{lastThread.threadTitle}</div>
            )}
            {lastThread.avatar && (
              <img
                src={lastThread.avatar}
                alt=""
                className={styles.sidebarAvatar}
              />
            )}
            <div className={styles.sidebarAuthor}>
              {lastThread.character || lastThread.author}
            </div>
            {lastThread.date && (
              <div className={styles.sidebarDate}>{formatDate(lastThread.date)}</div>
            )}
          </div>
        ) : (
          <div className={styles.cardSidebar}>
            <div className={styles.sidebarLabel}>Activité</div>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={styles.noActivityIcon}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div className={styles.sidebarAuthor}>Aucune activité</div>
          </div>
        )}
      </div>
    </Link>
  );
};

export default ElseworldCard;
